/**
 * Apple Sign-In Integration
 * Loads Apple JS SDK, initializes with client ID and redirect URI.
 * Uses popup flow: signIn() opens popup, callback page postMessages id_token back.
 */

import { loadScript, APPLE_SCRIPT_URL } from './loadScript';
import { generateNonce, sha256 } from './nonce';

declare global {
  interface Window {
    AppleID?: {
      auth: {
        init: (config: {
          clientId: string;
          scope?: string;
          redirectURI: string;
          state?: string;
          nonce?: string;
          usePopup?: boolean;
        }) => void;
        signIn: () => Promise<{
          authorization: {
            id_token: string;
            code?: string;
          };
          user?: {
            email?: string;
            name?: { firstName?: string; lastName?: string };
          };
        }>;
      };
    };
  }
}

/**
 * Load Apple Sign-In script and initialize with a fresh nonce
 * Re-initializes every call to ensure a unique nonce per sign-in attempt
 * redirectURI must be registered in Apple Developer and match the page domain
 */
export async function initAppleSignIn(
  clientId: string,
  redirectUri: string,
  nonceHash?: string
): Promise<void> {
  await loadScript(APPLE_SCRIPT_URL);

  if (!window.AppleID?.auth) {
    throw new Error('Apple Sign-In not available after script load');
  }

  window.AppleID.auth.init({
    clientId,
    scope: 'name email',
    redirectURI: redirectUri,
    usePopup: true,
    nonce: nonceHash,
  });
}

export interface AppleSignInResult {
  idToken: string;
  nonce?: string;
}

/**
 * Trigger Apple Sign-In flow
 * Generates a fresh nonce per attempt, passes its SHA-256 hash to Apple SDK,
 * and returns the raw nonce alongside the id_token for backend verification
 */
export async function signInWithApple(): Promise<AppleSignInResult> {
  const clientId = import.meta.env.VITE_APPLE_CLIENT_ID;
  const redirectUri =
    import.meta.env.VITE_APPLE_REDIRECT_URI ||
    `${window.location.origin}/auth/callback/apple`;

  if (!clientId) {
    throw new Error('Apple Sign-In is not configured (missing VITE_APPLE_CLIENT_ID)');
  }

  // Generate fresh nonce for this sign-in attempt
  const rawNonce = generateNonce();
  const nonceHash = await sha256(rawNonce);

  await initAppleSignIn(clientId, redirectUri, nonceHash);

  const appleAuth = window.AppleID?.auth;
  if (!appleAuth) {
    throw new Error('Apple Sign-In not available');
  }

  const response = await appleAuth.signIn();

  if (!response?.authorization?.id_token) {
    throw new Error('Apple Sign-In did not return an ID token');
  }

  return {
    idToken: response.authorization.id_token,
    nonce: rawNonce,
  };
}
